// frontend/src/pages/LivestockPage.tsx

import { useState, useMemo, useCallback } from 'react';
import {
  Container,
  Stack,
  Title,
  Group,
  Button,
  TextInput,
  Select,
  Paper,
  Text,
  Loader,
  Alert,
  Box,
  SegmentedControl,
  ActionIcon,
} from '@mantine/core';
import { IconPlus, IconSearch, IconAlertCircle, IconFish, IconFileImport, IconPhotoPlus } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useTank } from '@/contexts/TankContext';
import { NoTankSelected } from '@/components/common/NoTankSelected';
import { useLivestockList, useUpdateLivestockStatus, useDeleteLivestock } from '@/hooks/useLivestock';
import { LivestockTable, LivestockCard, AddLivestockModal, EditLivestockModal, LivestockTimeline, LivestockTypeFilter } from '@/features/livestock';
import { AddPhotosButton } from '@/features/livestock/AddPhotosButton';
import { BRAND_TEXT } from '@/features/livestock/brandText';
import { PhotosToSortRow } from '@/features/livestock/PhotosToSortRow';
import { TankShotsRow } from '@/features/livestock/TankShotsRow';
import { LIVESTOCK_TYPE_META } from '@/features/livestock/constants';
import type { Livestock, LivestockType } from '@/types/livestock';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useInboxCount } from '@/hooks/usePhotoInbox';

export function LivestockPage() {
  const { selectedTank, refreshTanks } = useTank();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [addModalOpened, setAddModalOpened] = useState(false);
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [editingLivestock, setEditingLivestock] = useState<Livestock | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState<'list' | 'timeline'>('list');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useLivestockList({
    status: statusFilter || undefined,
    search: searchQuery || undefined,
  });

  // Photos waiting in the tank's inbox (plan 4.2)
  const { data: photosToSort = 0 } = useInboxCount();

  const updateStatusMutation = useUpdateLivestockStatus();
  const deleteMutation = useDeleteLivestock();

  // Calculate current stocking (sum of quantities of alive fish only)
  const currentStocking = useMemo(() => {
    if (!data?.livestock) return 0;
    return data.livestock
      .filter(item => item.status === 'alive' && item.livestock_type === 'fish')
      .reduce((sum, item) => sum + item.quantity, 0);
  }, [data?.livestock]);

  const filteredLivestock = useMemo(() => {
    if (!data?.livestock) return [];
    if (!typeFilter) return data.livestock;
    return data.livestock.filter((item) => item.livestock_type === typeFilter);
  }, [data?.livestock, typeFilter]);

  const handleEdit = (livestock: Livestock) => {
    setEditingLivestock(livestock);
    setEditModalOpened(true);
  };

  const handleRowClick = useCallback((livestock: Livestock) => {
    navigate(`/tanks/${selectedTank!.id}/livestock/${livestock.id}`);
  }, [navigate, selectedTank]);

  const handleMarkDeceased = (livestock: Livestock) => {
    updateStatusMutation.mutate({
      id: livestock.id,
      data: {
        status: 'deceased',
        deceased_date: new Date().toISOString().split('T')[0],
      }
    });
  };

  const handleDelete = (livestock: Livestock) => {
    if (confirm(`Delete ${livestock.common_name}?`)) {
      deleteMutation.mutate(livestock.id);
    }
  };

  const handleSuccess = () => {
    refetch();
    setAddModalOpened(false);
  };

  if (!selectedTank) {
    return (
      <Container size="lg" py="xl">
        <NoTankSelected purpose="to view its livestock" />
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <Group gap="xs">
            <IconFish size={28} />
            <Title order={1}>
              Livestock
              {data && (
                <Text component="span" size="lg" c="dimmed" fw={400}>
                  {' '}(current stocking: {currentStocking} fish)
                </Text>
              )}
            </Title>
          </Group>
          {/* One row on a 360 px phone: Import is an icon there. */}
          <Group gap="xs" wrap="nowrap">
            {isMobile ? (
              <ActionIcon
                variant="light"
                size={36}
                aria-label="Import livestock"
                onClick={() => navigate('/livestock/import')}
              >
                <IconFileImport size={16} />
              </ActionIcon>
            ) : (
              <Button
                variant="light"
                leftSection={<IconFileImport size={16} />}
                onClick={() => navigate('/livestock/import')}
              >
                Import
              </Button>
            )}
            {/* Photos go to the tank's inbox through the upload queue (plan 4.1). The count
                is on the nav item and the row below, not here: this opens the photo picker. */}
            <AddPhotosButton>
              {(props): JSX.Element => (
                <Button
                  {...props}
                  variant="light"
                  leftSection={<IconPhotoPlus size={16} />}
                  style={BRAND_TEXT}
                >
                  Add Photos
                </Button>
              )}
            </AddPhotosButton>
            <Button leftSection={<IconPlus size={16} />} onClick={() => setAddModalOpened(true)}>
              {isMobile ? 'Add' : 'Add Livestock'}
            </Button>
          </Group>
        </Group>

        {photosToSort > 0 && <PhotosToSortRow count={photosToSort} />}

        {/* Always there (design 6.3): the cover and count come with the tanks list. */}
        <TankShotsRow
          tankId={selectedTank.id}
          count={selectedTank.tank_shot_count ?? 0}
          thumbUrl={selectedTank.cover_thumb_url ?? null}
          onImageError={() => void refreshTanks?.()}
        />

        <Paper p="md" withBorder>
          <Group>
            <Select
              placeholder="All Status"
              data={[
                { value: '', label: 'All Status' },
                { value: 'alive', label: 'Alive' },
                { value: 'deceased', label: 'Deceased' },
                { value: 'sold', label: 'Sold' },
              ]}
              value={statusFilter}
              onChange={setStatusFilter}
              clearable
              style={{ width: 150 }}
            />
            <TextInput
              placeholder="Search by name..."
              leftSection={<IconSearch size={16} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
          </Group>
        </Paper>

        <LivestockTypeFilter value={typeFilter} onChange={setTypeFilter} />

        <SegmentedControl
          value={view}
          onChange={(value) => setView(value as 'list' | 'timeline')}
          data={[
            { label: 'List', value: 'list' },
            { label: 'Timeline', value: 'timeline' },
          ]}
        />

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            Failed to load livestock: {(error as Error).message}
          </Alert>
        )}

        {isLoading && (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        )}

        {!isLoading && !error && data && (
          <>
            {filteredLivestock.length === 0 ? (
              <Paper p="xl" withBorder ta="center">
                <Stack align="center" gap="md">
                  {(() => {
                    const hasActiveFilters = !!searchQuery || !!statusFilter;
                    const meta = typeFilter ? LIVESTOCK_TYPE_META[typeFilter as LivestockType] : null;
                    const EmptyIcon = meta?.Icon ?? IconFish;

                    if (hasActiveFilters) {
                      return (
                        <>
                          <EmptyIcon size={48} color="gray" />
                          <Text c="dimmed">No matching results</Text>
                        </>
                      );
                    }

                    return (
                      <>
                        <EmptyIcon size={48} color="gray" />
                        <Text c="dimmed">No livestock found</Text>
                        <Button variant="light" onClick={() => setAddModalOpened(true)}>
                          {meta ? `Add your first ${meta.label.toLowerCase()}` : 'Add livestock'}
                        </Button>
                      </>
                    );
                  })()}
                </Stack>
              </Paper>
            ) : view === 'timeline' ? (
              <Paper p="md" withBorder>
                <LivestockTimeline livestock={filteredLivestock} onItemClick={handleRowClick} />
              </Paper>
            ) : (
              <>
                <Box visibleFrom="sm">
                  <LivestockTable
                    livestock={filteredLivestock}
                    tankId={selectedTank.id}
                    typeFilter={typeFilter}
                    onEdit={handleEdit}
                    onMarkDeceased={handleMarkDeceased}
                    onDelete={handleDelete}
                  />
                </Box>
                <Box hiddenFrom="sm">
                  <Stack gap="sm">
                    {filteredLivestock.map((item) => (
                      <LivestockCard
                        key={item.id}
                        livestock={item}
                        tankId={selectedTank.id}
                      />
                    ))}
                  </Stack>
                </Box>
              </>
            )}
          </>
        )}
      </Stack>

      <AddLivestockModal
        opened={addModalOpened}
        onClose={() => setAddModalOpened(false)}
        onSuccess={handleSuccess}
        tankId={selectedTank.id}
        defaultType={typeFilter as import('@/types/livestock').LivestockType | null}
      />

      <EditLivestockModal
        opened={editModalOpened}
        onClose={() => {
          setEditModalOpened(false);
          setEditingLivestock(null);
        }}
        livestock={editingLivestock}
      />
    </Container>
  );
}
